import React, { useState, useEffect, useMemo } from 'react';
import { useItems } from '../context/DataContext';
import './ViewStock.css';

function ViewStock() {
  const { data: items, loading } = useItems();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSize, setFilterSize] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortField, setSortField] = useState('totalWeight');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('table');
  const [stockSummary, setStockSummary] = useState([]);

  useEffect(() => {
    if (items && items.length > 0) {
      setStockSummary(items);
    }
  }, [items]);

  const handleSort = (field) => {
    const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newOrder);
  };

  const getUniqueSizes = () => {
    const sizes = new Set(stockSummary.map(item => item.size));
    return Array.from(sizes).filter(Boolean).sort();
  };

  const getStatusCount = () => {
    const counts = { all: stockSummary.length, inStock: 0, lowStock: 0, outOfStock: 0 };
    stockSummary.forEach(item => {
      if (item.numberOfPackets === 0) counts.outOfStock++;
      else if (item.numberOfPackets < 5) counts.lowStock++;
      else counts.inStock++;
    });
    return counts;
  };

  const filteredStock = useMemo(() => {
    let filtered = [...stockSummary];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.itemCode?.toLowerCase().includes(term) ||
        item.itemName?.toLowerCase().includes(term)
      );
    }

    if (filterSize) {
      filtered = filtered.filter(item => item.size === filterSize);
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(item => {
        if (filterStatus === 'inStock') return item.numberOfPackets >= 5;
        if (filterStatus === 'lowStock') return item.numberOfPackets > 0 && item.numberOfPackets < 5;
        if (filterStatus === 'outOfStock') return item.numberOfPackets === 0;
        return true;
      });
    }

    filtered.sort((a, b) => {
      let aVal = a[sortField] || 0;
      let bVal = b[sortField] || 0;
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [stockSummary, searchTerm, filterSize, filterStatus, sortField, sortOrder]);

  const statusCounts = getStatusCount();

  const getStatusInfo = (packets) => {
    if (packets === 0) return { label: 'Out of Stock', className: 'out-of-stock' };
    if (packets < 5) return { label: 'Low Stock', className: 'low-stock' };
    return { label: 'In Stock', className: 'in-stock' };
  };

  if (loading) {
    return (
      <div className="view-stock-page">
        <div className="loading"><div className="loading-spinner"></div><p>Loading stock data...</p></div>
      </div>
    );
  }

  return (
    <div className="view-stock-page">
      <div className="page-header">
        <h1 className="page-title">View Stock</h1>
        <div className="stock-count">
          Total Items: <strong>{stockSummary.length}</strong>
        </div>
      </div>

      <div className="status-summary">
        <div className="status-item all">
          <span className="status-label">All</span>
          <span className="status-count">{statusCounts.all}</span>
        </div>
        <div className="status-item in-stock">
          <span className="status-label">✅ In Stock</span>
          <span className="status-count">{statusCounts.inStock}</span>
        </div>
        <div className="status-item low-stock">
          <span className="status-label">⚠️ Low Stock</span>
          <span className="status-count">{statusCounts.lowStock}</span>
        </div>
        <div className="status-item out-of-stock">
          <span className="status-label">❌ Out of Stock</span>
          <span className="status-count">{statusCounts.outOfStock}</span>
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <input
            type="text"
            className="form-control"
            placeholder="🔍 Search by code or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select
          className="form-control"
          value={filterSize}
          onChange={(e) => setFilterSize(e.target.value)}
        >
          <option value="">All Sizes</option>
          {getUniqueSizes().map(size => (
            <option key={size} value={size}>{size}</option>
          ))}
        </select>

        <select
          className="form-control"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="inStock">In Stock</option>
          <option value="lowStock">Low Stock</option>
          <option value="outOfStock">Out of Stock</option>
        </select>

        <div className="view-toggle">
          <button
            className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => setViewMode('table')}
          >
            📋 Table
          </button>
          <button
            className={`view-btn ${viewMode === 'cards' ? 'active' : ''}`}
            onClick={() => setViewMode('cards')}
          >
            🃏 Cards
          </button>
        </div>
      </div>

      <div className="results-info">
        Showing {filteredStock.length} of {stockSummary.length} items
      </div>

      {viewMode === 'table' && (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th onClick={() => handleSort('itemCode')}>
                  Code {sortField === 'itemCode' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('itemName')}>
                  Name {sortField === 'itemName' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('size')}>
                  Size {sortField === 'size' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('weightPerPcs')}>
                  Weight/Pcs {sortField === 'weightPerPcs' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('totalWeight')}>
                  Total Weight {sortField === 'totalWeight' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('numberOfPackets')}>
                  Packets {sortField === 'numberOfPackets' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredStock.length === 0 ? (
                <tr><td colSpan="7" className="no-data">No items found</td></tr>
              ) : (
                filteredStock.map((item) => {
                  const status = getStatusInfo(item.numberOfPackets);
                  return (
                    <tr key={item.id || `${item.itemCode}-${item.size}`}>
                      <td><strong>{item.itemCode}</strong></td>
                      <td>{item.itemName}</td>
                      <td>{item.size}</td>
                      <td>{item.weightPerPcs?.toFixed(2) || '-'}</td>
                      <td>{item.totalWeight?.toFixed(2)} g</td>
                      <td>{item.numberOfPackets}</td>
                      <td><span className={`status-badge ${status.className}`}>{status.label}</span></td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {viewMode === 'cards' && (
        <div className="cards-grid">
          {filteredStock.length === 0 ? (
            <p className="no-data">No items found</p>
          ) : (
            filteredStock.map((item) => {
              const status = getStatusInfo(item.numberOfPackets);
              return (
                <div key={item.id || `${item.itemCode}-${item.size}`} className="stock-card">
                  <div className="card-header">
                    <h3>{item.itemName}</h3>
                    <span className={`status-badge ${status.className}`}>{status.label}</span>
                  </div>
                  <div className="card-body">
                    <div className="card-detail"><span className="label">Code:</span><span className="value">{item.itemCode}</span></div>
                    <div className="card-detail"><span className="label">Size:</span><span className="value">{item.size}</span></div>
                    <div className="card-detail"><span className="label">Weight/Pcs:</span><span className="value">{item.weightPerPcs?.toFixed(2) || '-'} g</span></div>
                    <div className="card-detail"><span className="label">Total Weight:</span><span className="value">{item.totalWeight?.toFixed(2)} g</span></div>
                    <div className="card-detail"><span className="label">Packets:</span><span className="value">{item.numberOfPackets}</span></div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export default ViewStock;